// Spec: .claude/plans/adaptive-diagnostics/part-07-student-taking/ac.md.
// Reason priority below mirrors the spec — keep in sync.

export type DiagnosticUnavailableReason =
  | 'not-yet-started'
  | 'time-window-closed'
  | 'timer-expired'
  | 'completed';

export type DiagnosticAvailability =
  | {open: true}
  | {open: false; reason: DiagnosticUnavailableReason};

export interface AvailabilityInput {
  completedAt: string | null | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
}

// Returns null for missing input; throws for unparseable. Callers should fail closed on throw.
const parseDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) throw new Error(`Invalid diagnostic date: ${s}`);
  return d;
};

export const diagnosticAvailability = (
  input: AvailabilityInput,
  now: Date = new Date()
): DiagnosticAvailability => {
  let start: Date | null;
  let end: Date | null;
  try {
    start = parseDate(input.startDate);
    end = parseDate(input.endDate);
  } catch {
    // Fail closed on malformed dates so the student isn't silently admitted.
    return {open: false, reason: 'time-window-closed'};
  }
  if (start && start > now) return {open: false, reason: 'not-yet-started'};
  if (end && end <= now) return {open: false, reason: 'time-window-closed'};
  if (input.completedAt) return {open: false, reason: 'completed'};
  return {open: true};
};

export const isOpenDiagnostic = (input: AvailabilityInput, now: Date = new Date()): boolean =>
  diagnosticAvailability(input, now).open;
